import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import FilterBar from "./FilterBar";
import SummaryTable from "./SummaryTable";
import {
  getCategorySummary,
  getCategoryMembers,
  getChaptersList,
} from "./categoryWiseMembersService";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import {
  styleExcelHeaderRow,
  styleExcelTitleRow,
} from "../../../utils/excelStyles";
import "./CategoryWiseMembers.css";

const CategoryWiseMembers = () => {
  // Filters state
  const [chapterId, setChapterId] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [chapterOptions, setChapterOptions] = useState([]);

  // Categories summary state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expand / nested cache state
  const [expandedCategoryIds, setExpandedCategoryIds] = useState([]);
  const [membersCache, setMembersCache] = useState({});

  // Export buttons disabled state
  const [btnExcelDisabled, setBtnExcelDisabled] = useState(false);
  const [btnPdfDisabled, setBtnPdfDisabled] = useState(false);

  // Debounce search input (300 ms)
  const debounceTimerRef = useRef(null);
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  // Fetch Chapter options from DB on mount
  useEffect(() => {
    const fetchChapters = async () => {
      const options = await getChaptersList();
      setChapterOptions(options);
    };
    fetchChapters();
  }, []);

  // Load category summary data from live DB API
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCategorySummary({
        chapterId,
        status,
        search: debouncedSearch,
      });
      setCategories(data);
    } catch (err) {
      setError(err.message || "Something went wrong while fetching categories.");
    } finally {
      setLoading(false);
    }
  }, [chapterId, status, debouncedSearch]);

  // When chapterId or status changes, clear member cache
  useEffect(() => {
    setMembersCache({});
  }, [chapterId, status]);

  // Trigger summary fetch when filters change
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Handle expand/collapse toggle for a category row
  const handleToggleExpand = async (categoryId, forceRefetch = false) => {
    const isAlreadyExpanded = expandedCategoryIds.includes(categoryId);

    if (isAlreadyExpanded && !forceRefetch) {
      // Collapse
      setExpandedCategoryIds((prev) => prev.filter((id) => id !== categoryId));
      return;
    }

    // Expand
    if (!isAlreadyExpanded) {
      setExpandedCategoryIds((prev) => [...prev, categoryId]);
    }

    // Check if already in cache and not forcing refetch
    if (membersCache[categoryId]?.data && !forceRefetch) {
      return;
    }

    // Fetch members with cache
    setMembersCache((prev) => ({
      ...prev,
      [categoryId]: { loading: true, error: null, data: prev[categoryId]?.data || [] },
    }));

    try {
      const members = await getCategoryMembers(categoryId, {
        chapterId,
        status,
      });
      setMembersCache((prev) => ({
        ...prev,
        [categoryId]: { loading: false, error: null, data: members },
      }));
    } catch (err) {
      setMembersCache((prev) => ({
        ...prev,
        [categoryId]: {
          loading: false,
          error: err.message || "Failed to load members",
          data: [],
        },
      }));
    }
  };

  // Excel Export
  const exportAsExcel = async () => {
    try {
      if (categories.length < 1) {
        toast.error("No data found!");
        return;
      }
      setBtnExcelDisabled(true);
      const workbook = new Excel.Workbook();
      const fileName = "Category Wise Members Report";
      const worksheet = workbook.addWorksheet(fileName);

      const columns = [
        { header: "Sl.No.", key: "sno" },
        { header: "Category", key: "category_name" },
        { header: "Total Members", key: "total_members" },
        { header: "Active Members", key: "active_members" },
        { header: "Inactive Members", key: "inactive_members" },
      ];

      worksheet.columns = columns;
      worksheet.insertRow(1, []);

      const totalCols = columns.length;
      const lastColLetter = String.fromCharCode(64 + totalCols);
      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = fileName;
      styleExcelTitleRow(worksheet.getRow(1), totalCols);

      const headerRow = worksheet.getRow(2);
      styleExcelHeaderRow(headerRow, totalCols);

      worksheet.columns.forEach((column) => {
        column.width = 25;
        column.alignment = { horizontal: "center" };
      });

      categories.forEach((cat, index) => {
        worksheet.addRow({
          sno: index + 1,
          category_name: cat.category_name,
          total_members: cat.total_members,
          active_members: cat.active_members,
          inactive_members: cat.inactive_members,
        });
      });

      worksheet.eachRow({ includeEmpty: false }, (row) => {
        row._cells.forEach((singleCell) => {
          worksheet.getCell(singleCell._address).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `${fileName}_${moment().format("DD-MM-YYYY")}.xlsx`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel");
    } finally {
      setBtnExcelDisabled(false);
    }
  };

  // PDF Export
  const exportAsPDF = async () => {
    try {
      if (categories.length < 1) {
        toast.error("No data found!");
        return;
      }
      setBtnPdfDisabled(true);
      const doc = new jsPDF("portrait");
      const headers = ["Sl.No.", "Category", "Total", "Active", "Inactive"];

      const pdfData = categories.map((cat, index) => [
        index + 1,
        cat.category_name,
        cat.total_members,
        cat.active_members,
        cat.inactive_members,
      ]);

      const pdfTitle = "Category Wise Members Report";
      const textWidth =
        (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor;
      const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
      doc.text(pdfTitle, textOffset, 15);

      doc.autoTable({
        head: [headers],
        body: pdfData,
        margin: { top: 25, right: 15, bottom: 20, left: 15 },
        headerStyles: {
          fillColor: [43, 52, 69],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
      });

      doc.save(`Category_Wise_Members_${moment().format("DD-MM-YYYY")}.pdf`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export PDF");
    } finally {
      setBtnPdfDisabled(false);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-3">
        {/* Page Header & Breadcrumbs matching Reports standard */}
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Chapter members: category wise</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span>Reports</span>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Chapter members: category wise
                  </span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <button
                  disabled={btnExcelDisabled}
                  type="button"
                  onClick={exportAsExcel}
                  className="btn rounded-pill btn-primary excel-btn ibutton"
                >
                  <span className="tf-icons bx bxs-spreadsheet"></span>
                  &nbsp; Excel
                </button>
                <button
                  disabled={btnPdfDisabled}
                  type="button"
                  onClick={exportAsPDF}
                  className="btn rounded-pill btn-primary pdf-btn ibutton"
                >
                  <span className="tf-icons bx bxs-file-pdf"></span>&nbsp;
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        {/* Filter Card matching other Reports standard */}
        <FilterBar
          chapterId={chapterId}
          onChapterChange={setChapterId}
          status={status}
          onStatusChange={setStatus}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          chapterOptions={chapterOptions}
        />

        {/* Standard Reports Sticky Table with expand support */}
        <SummaryTable
          categories={categories}
          loading={loading}
          error={error}
          onRetry={fetchSummary}
          expandedCategoryIds={expandedCategoryIds}
          onToggleExpand={handleToggleExpand}
          membersCache={membersCache}
        />
      </div>
    </div>
  );
};

export default CategoryWiseMembers;
